import { useMutation } from '@apollo/client'
import { useTheme } from '@emotion/react'
import { Edit, PushPin, PushPinOutlined, UnfoldMore } from '@mui/icons-material'
import {
    Box,
    Button,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material'
import StarterKit from '@tiptap/starter-kit'
import { formatDate } from 'date-fns'
import { RichTextReadOnly } from 'mui-tiptap'
import { Fragment, useRef, useState } from 'react'
import { CompanyNote, PinCompanyNote } from 'recruiter/graphql/companyNotes.gql'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'
import { useNotesContext } from 'shared/utils/noteContext'
import EditNotes from './EditNotes'
import { StyledBox, StyledCard } from './styled'

enum SORT_ORDER {
    newest = 'newest',
    oldest = 'oldest',
    title = 'title',
}

const Notes = ({ data, refetch }) => {
    const [pinCompanyNote] = useMutation(PinCompanyNote)
    const theme = useTheme()
    const toast = useToast()
    const [sortOrder, setSortOrder] = useState<SORT_ORDER | null>(null)
    const sortRef = useRef(null)
    const [sortMenuOpen, setSortMenuOpen] = useState(false)
    const [searchValue, setSearchValue] = useState('')

    const sortAndFilterNotes = (notes: CompanyNote[]) => {
        if (!notes) return []
        let notesToModify = [...notes]

        if (searchValue) {
            notesToModify = notesToModify.filter(
                (note) =>
                    note.noteData
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) ||
                    note.title.toLowerCase().includes(searchValue.toLowerCase())
            )
        }
        if (sortOrder) {
            notesToModify.sort((a, b) => {
                if (sortOrder === 'newest') {
                    return a.updatedAt < b.updatedAt ? 1 : -1
                } else if (sortOrder === 'oldest') {
                    return b.updatedAt < a.updatedAt ? 1 : -1
                } else {
                    return a.title < b.title ? -1 : 1
                }
            })
        }
        return notesToModify
    }

    const handleSortSelection = (newSortOrder: SORT_ORDER) => {
        setSortMenuOpen(false)
        if (sortOrder !== newSortOrder) {
            setSortOrder(newSortOrder)
        } else {
            setSortOrder(null)
        }
    }

    const pinNote = async (noteId: string) => {
        await pinCompanyNote({
            variables: {
                noteId,
            },
            onCompleted: () => {
                refetch()
            },
            onError: (error) => {
                console.error(error)
                toast({
                    title: 'Error!',
                    message: 'Something went wrong. Please try again later.',
                    severity: 'error',
                })
            },
        })
        refetch()
    }

    const visibleNotes = sortAndFilterNotes(data?.company?.notes)
    const { isEditingNote, selectedNote, closeEditNote, openEditNote } =
        useNotesContext()

    return (
        <>
            {(!selectedNote || selectedNote.__typename === 'CompanyNote') && (
                <EditNotes
                    refetch={refetch}
                    open={isEditingNote}
                    note={selectedNote}
                    onClose={closeEditNote}
                />
            )}
            <StyledCard>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: getPxAsRem(16),
                    }}
                >
                    <Typography variant="h4">Notes</Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEditNote(null)}
                    >
                        Add Note
                    </Button>
                </Box>
                <StyledBox>
                    <Box>
                        <IconButton
                            ref={sortRef}
                            onClick={() => setSortMenuOpen((prev) => !prev)}
                            color={sortOrder ? 'primary' : 'default'}
                            sx={{
                                backgroundColor: sortMenuOpen
                                    ? `${theme.palette.primary.light} !important`
                                    : undefined,
                            }}
                        >
                            <UnfoldMore />
                        </IconButton>
                        <Menu
                            open={sortMenuOpen}
                            anchorEl={sortRef.current}
                            onClose={() => setSortMenuOpen(false)}
                        >
                            <MenuItem
                                onClick={() =>
                                    handleSortSelection(SORT_ORDER.newest)
                                }
                                selected={sortOrder === SORT_ORDER.newest}
                            >
                                Newest
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    handleSortSelection(SORT_ORDER.oldest)
                                }
                                selected={sortOrder === SORT_ORDER.oldest}
                            >
                                Oldest
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    handleSortSelection(SORT_ORDER.title)
                                }
                                selected={sortOrder === SORT_ORDER.title}
                            >
                                Title
                            </MenuItem>
                        </Menu>
                    </Box>
                    <TextField
                        label="Search"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                </StyledBox>
                <Box>
                    {visibleNotes
                        .filter((n) => n.isActive)
                        .map((note, index) => (
                            <Fragment key={note.id}>
                                <Box sx={{ padding: getPxAsRem(8, 0) }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight="bold"
                                        >
                                            {note.title}
                                        </Typography>
                                        <Box>
                                            <IconButton
                                                onClick={() => pinNote(note.id)}
                                            >
                                                {note.isPinned ? (
                                                    <PushPin />
                                                ) : (
                                                    <PushPinOutlined />
                                                )}
                                            </IconButton>
                                            <IconButton
                                                onClick={() =>
                                                    openEditNote(note)
                                                }
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2">{`${formatDate(new Date(note.updatedAt || note.createdAt), 'MM/dd/yyyy h:mm a')}${note.updatedBy ? ` • ${note.updatedByUser?.firstName} ${note.updatedByUser?.lastName}` : note.createdBy ? ` • ${note.createdByUser?.firstName} ${note.createdByUser?.lastName}` : ''}`}</Typography>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        component="div"
                                        sx={{
                                            display: '-webkit-box',
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            WebkitLineClamp: 3,
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        <RichTextReadOnly
                                            extensions={[StarterKit]}
                                            content={note.noteData}
                                        />
                                    </Typography>
                                </Box>
                                {index < data.company?.notes?.length - 1 && (
                                    <Divider />
                                )}
                            </Fragment>
                        ))}
                    {data.company?.notes?.length === 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginTop: getPxAsRem(16),
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    margin: 'auto',
                                    color: theme.palette.other.secondary_light,
                                }}
                            >
                                No Notes Available
                            </Typography>
                        </Box>
                    )}
                </Box>
            </StyledCard>
        </>
    )
}

export default Notes
